import {LocalStorageAppenderConfig} from './local-storage-appender-config';
import {LoggerAppender} from '../logger-appender';
import {LogMessage} from '../../log-message';
import {LogLevel} from '../../log-level';
import {LocalStorageProvider} from '../../../storage-service';
import {ConfigService} from '../../../config/config-service';
import {inject, Injectable} from '../../../di';
import {filter, map} from 'rxjs';

const DEFAULT_TRIGGER_LEVEL: LogLevel = LogLevel.Warn;
const DEFAULT_CONTEXT_SIZE: number = 30;
const DEFAULT_STORAGE_LIMIT: number = 30;

/**
 * LocalStorage logger appender
 */
@Injectable()
export class LocalStorageAppender extends LoggerAppender {
  private readonly configService = inject(ConfigService);

  protected config!: LocalStorageAppenderConfig;
  protected chunksToSave: LoggerLocalStorageChunk[] = [];

  protected get ls(): LocalStorageProvider {
    return this.configService.config?.storage?.localStorage || window.androidStorage || localStorage;
  }

  constructor() {
    super();
    this.configService.config$
      .pipe(
        map((c) => c?.logger?.localStorage),
        filter((c) => !!c),
      )
      .subscribe((c) => this.applyConfig(c));

    window.addEventListener('beforeunload', () => this.dropToLocalStorage(), false);
    // window.addEventListener('unload', () => this.dropToLocalStorage(), false);
    window.addEventListener('pagehide', () => this.dropToLocalStorage(), false);

    setInterval(() => {
      this.dropToLocalStorage();
    }, 5000);
  }

  private applyConfig(config?: LocalStorageAppenderConfig) {
    // validation and fixing config:
    if (!config) {
      // Default config
      config = new LocalStorageAppenderConfig();
    }
    if (config.triggerLevel == null) {
      config.triggerLevel = DEFAULT_TRIGGER_LEVEL;
    }
    if (config.contextSize == null || config.contextSize <= 0) {
      config.contextSize = DEFAULT_CONTEXT_SIZE;
    }
    if (config.size == null || config.size <= 0) {
      config.size = DEFAULT_STORAGE_LIMIT;
    }

    // applying config
    this.config = config;
  }

  public log(message: LogMessage, level: LogLevel, args: any[], history: LogMessage[]): void {
    if (!this.config.enabled || level < this.config.triggerLevel) {
      return;
    }

    // store messages
    const chunk: LoggerLocalStorageChunk = {
      message: message,
    };

    if (this.config.contextSize > 0) {
      const context = history.slice(-this.config.contextSize);
      if (context && context.length > 0) {
        chunk.context = context;
      }
    }

    this.chunksToSave.push(chunk);
    if (this.chunksToSave.length > this.config.size) {
      this.chunksToSave.shift();
    }
  }

  public dropToLocalStorage() {
    if (!this.config.enabled || this.chunksToSave.length === 0) {
      return;
    }

    let chunksToSave = this.chunksToSave;
    this.chunksToSave = [];
    try {
      const lsValue = this.ls.getItem(this.config.localStorageKey);
      const oldChunks = lsValue ? (JSON.parse(lsValue) as LoggerLocalStorageChunk[]) || [] : [];
      chunksToSave = oldChunks.concat(chunksToSave);
      if (chunksToSave.length > this.config.size) {
        chunksToSave = chunksToSave.slice(chunksToSave.length - this.config.size - 1, chunksToSave.length - 1);
      }
      this.ls.setItem(this.config.localStorageKey, JSON.stringify(chunksToSave));
    } catch (ex) {
      console.warn('[Logger] Unable to save logs to localStorage', ex);
    }
  }

  /**
   * Get all preserved logs from LocalStorageAppender
   */
  public getMessages(): LoggerLocalStorageChunk[] {
    try {
      const lsValue = this.ls.getItem(this.config.localStorageKey);
      const storedChunks = lsValue ? (JSON.parse(lsValue) as LoggerLocalStorageChunk[]) || [] : [];
      return this.chunksToSave.concat(storedChunks);
    } catch (ex) {
      console.warn('[Logger] Unable to get logs in localStorageAppender storage', ex);
      return [];
    }
  }
}

interface LoggerLocalStorageChunk {
  message: LogMessage;
  context?: LogMessage[];
}
